import { useState, useEffect } from 'react';
import { Movie } from '../types/movie';
import { TMDBMovieDetail, getMovieDetails, searchMovies, getPosterUrl, getBackdropUrl, getImdbUrl, getTmdbUrl } from '../services/tmdbApi';
import { X, Star, Clock, Calendar, Heart } from 'lucide-react';

interface MovieDetailProps {
  movie: Movie;
  onClose: () => void;
}

export const MovieDetail: React.FC<MovieDetailProps> = ({ movie, onClose }) => {
  const [tmdbMovie, setTmdbMovie] = useState<TMDBMovieDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMovieDetails = async () => {
      setLoading(true);
      setError(null);
      
      try {
        let details: TMDBMovieDetail | null = null;
        
        // If we have a TMDB ID, fetch details directly
        if (movie.tmdbId) {
          details = await getMovieDetails(movie.tmdbId);
        } else {
          // If no TMDB ID, search for the movie by title and year
          const searchResults = await searchMovies(movie.title, movie.year);
          if (searchResults.length > 0) {
            // Get the first (most relevant) result
            const firstResult = searchResults[0];
            details = await getMovieDetails(firstResult.id);
          }
        }
        
        setTmdbMovie(details);
      } catch (err) {
        console.error('Error fetching movie details:', err);
        setError('Failed to load movie details from TMDB');
      } finally {
        setLoading(false);
      }
    };

    fetchMovieDetails();
  }, [movie.tmdbId, movie.title, movie.year]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Handle click outside modal
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 7) return 'text-blue-600';
    if (score >= 6) return 'text-yellow-600';
    if (score >= 5) return 'text-orange-600';
    return 'text-red-600';
  };

  const formatRuntime = (minutes?: number) => {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getDirector = () => {
    if (tmdbMovie?.credits?.crew) {
      const director = tmdbMovie.credits.crew.find(person => person.job === 'Director');
      return director?.name || movie.director;
    }
    return movie.director;
  };

  const getCast = () => {
    return tmdbMovie?.credits?.cast?.slice(0, 10) || [];
  };

  // Get IMDb ID from TMDB data or movie data
  const getImdbId = () => {
    return tmdbMovie?.external_ids?.imdb_id || movie.imdbId;
  };

  // Get TMDB ID
  const getTmdbId = () => {
    return movie.tmdbId || tmdbMovie?.id;
  };

  return (
    <div 
      className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative mx-3">
        {/* Close button - positioned absolutely in top-right corner */}
        <button
          onClick={onClose}
          className="absolute top-3 sm:top-4 right-3 sm:right-4 z-20 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-1.5 sm:p-2 shadow-lg transition-all duration-200 hover:scale-110"
          aria-label="Close movie details"
        >
          <X className="h-5 w-5 sm:h-6 sm:w-6 text-gray-700" />
        </button>

        {/* Header */}
        <div className="relative">
          {/* Backdrop */}
          {tmdbMovie?.backdrop_path && (
            <div className="relative h-32 sm:h-48 lg:h-64 bg-cover bg-center rounded-t-xl" 
                 style={{ backgroundImage: `url(${getBackdropUrl(tmdbMovie.backdrop_path)})` }}>
              <div className="absolute inset-0 bg-black bg-opacity-50"></div>
            </div>
          )}
        </div>

        <div className="p-3 sm:p-4 lg:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8 sm:py-12">
              <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-primary-600"></div>
              <div className="ml-3 text-gray-600 text-sm sm:text-base">Loading movie details from TMDB...</div>
            </div>
          ) : error ? (
            <div className="text-center py-8 sm:py-12 text-red-600 text-sm sm:text-base">{error}</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Poster and basic info */}
              <div className="lg:col-span-1">
                <div className="lg:sticky lg:top-0">
                  {tmdbMovie?.poster_path ? (
                    <img
                      src={getPosterUrl(tmdbMovie.poster_path, 'w500')}
                      alt={movie.title}
                      className="w-full rounded-lg shadow-lg"
                    />
                  ) : (
                    <div className="w-full h-64 sm:h-80 lg:h-96 bg-gray-200 rounded-lg flex items-center justify-center">
                      <span className="text-gray-500 text-sm sm:text-base">No poster available</span>
                    </div>
                  )}
                  
                  {/* External links */}
                  <div className="mt-3 sm:mt-4 space-y-2 sm:space-y-3">
                    {getImdbId() && (
                      <a
                        href={getImdbUrl(getImdbId()!)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-full py-2 px-3 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-lg transition-colors duration-200 text-sm sm:text-base"
                        title="View on IMDb"
                      >
                        <svg className="h-4 w-4 sm:h-5 sm:w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H4.476l7.698-9.19L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/>
                        </svg>
                        IMDb
                      </a>
                    )}
                    {getTmdbId() && (
                      <a
                        href={getTmdbUrl(getTmdbId()!)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-full py-2 px-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors duration-200 text-sm sm:text-base"
                        title="View on TMDB"
                      >
                        <svg className="h-4 w-4 sm:h-5 sm:w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 0C5.374 0 0 5.374 0 12s5.374 12 12 12 12-5.374 12-12S18.626 0 12 0zm5.568 17.568c-1.5 1.5-3.5 2.25-5.568 2.25s-4.068-.75-5.568-2.25c-1.5-1.5-2.25-3.5-2.25-5.568s.75-4.068 2.25-5.568c1.5-1.5 3.5-2.25 5.568-2.25s4.068.75 5.568 2.25c1.5 1.5 2.25 3.5 2.25 5.568s-.75 4.068-2.25 5.568z"/>
                        </svg>
                        TMDB
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Movie details */}
              <div className="lg:col-span-2">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{movie.title}</h1>
                
                {/* Tagline */}
                {tmdbMovie?.tagline && (
                  <p className="text-base sm:text-lg text-gray-600 italic mb-3 sm:mb-4">"{tmdbMovie.tagline}"</p>
                )}
                
                {/* Meta information */}
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-4 w-4" />
                    <span>{movie.year}</span>
                  </div>
                  {tmdbMovie?.runtime && (
                    <div className="flex items-center space-x-1">
                      <Clock className="h-4 w-4" />
                      <span>{formatRuntime(tmdbMovie.runtime)}</span>
                    </div>
                  )}
                  {tmdbMovie?.status && (
                    <div className="flex items-center space-x-1">
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                        {tmdbMovie.status}
                      </span>
                    </div>
                  )}
                  {movie.gf && (
                    <div className="flex items-center space-x-1 text-red-600">
                      <Heart className="h-4 w-4" />
                      <span>Watched with GF</span>
                    </div>
                  )}
                </div>

                {/* Ratings */}
                <div className="flex items-center space-x-6 mb-6">
                  <div className="flex items-center space-x-2">
                    <Star className={`h-5 w-5 ${getScoreColor(movie.score)}`} />
                    <span className={`font-semibold ${getScoreColor(movie.score)}`}>
                      Your Rating: {movie.score}/10
                    </span>
                  </div>
                  {tmdbMovie?.vote_average && (
                    <div className="flex items-center space-x-2">
                      <Star className="h-5 w-5 text-yellow-500" />
                      <span className="font-semibold text-gray-700">
                        TMDB: {tmdbMovie.vote_average.toFixed(1)}/10
                      </span>
                      <span className="text-sm text-gray-500">
                        ({tmdbMovie.vote_count} votes)
                      </span>
                    </div>
                  )}
                </div>

                {/* Overview */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Overview</h3>
                  <p className="text-gray-700 leading-relaxed">
                    {tmdbMovie?.overview || movie.plotSummary || 'No overview available.'}
                  </p>
                </div>

                {/* Genres */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Genres</h3>
                  <div className="flex flex-wrap gap-2">
                    {movie.genres.map((genre, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm"
                      >
                        {genre}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Director */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Director</h3>
                  <p className="text-gray-700">{getDirector()}</p>
                </div>

                {/* Cast */}
                {getCast().length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Cast</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {getCast().map((actor, index) => (
                        <div key={index} className="text-sm text-gray-700">
                          <span className="font-medium">{actor.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Production companies */}
                {tmdbMovie?.production_companies && tmdbMovie.production_companies.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Production</h3>
                    <div className="flex flex-wrap gap-2">
                      {tmdbMovie.production_companies.map((company, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                        >
                          {company.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Additional TMDB info */}
                {tmdbMovie && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Budget and Revenue */}
                    {(tmdbMovie.budget > 0 || tmdbMovie.revenue > 0) && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Financial Info</h3>
                        {tmdbMovie.budget > 0 && (
                          <div className="text-sm text-gray-700 mb-1">
                            <span className="font-medium">Budget:</span> ${tmdbMovie.budget.toLocaleString()}
                          </div>
                        )}
                        {tmdbMovie.revenue > 0 && (
                          <div className="text-sm text-gray-700">
                            <span className="font-medium">Revenue:</span> ${tmdbMovie.revenue.toLocaleString()}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Languages */}
                    {tmdbMovie.spoken_languages && tmdbMovie.spoken_languages.length > 0 && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Languages</h3>
                        <div className="flex flex-wrap gap-1">
                          {tmdbMovie.spoken_languages.map((lang, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-white text-gray-700 rounded text-xs"
                            >
                              {lang.english_name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
